import { useFormik } from 'formik';
import { capitalize, startCase, camelCase } from 'lodash';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Col, Row } from 'reactstrap';
import * as Yup from 'yup';
import PhoneInput from 'react-phone-input-2';
import Autocomplete from 'react-google-autocomplete';
import configObject from '../../../../config';

const validationSchema = Yup.object({
  destination: Yup.string().required('Please enter destination'),
  category: Yup.number().required('Please select category'),
  date_of_arrival: Yup.date().required('Please select date of arrival'),
  single_item_title: Yup.string().required('Please enter item name'),
  clearing_discharge_port: Yup.string().required('Please select port of discharge'),
  email: Yup.string()
    .email('Please enter valid email')
    .required('Please enter email'),
  phone: Yup.string(),
});

const BothServiceTab = ({ onSubmit }) => {
  const serviceData = useSelector((state) => state.enumData);

  const {
    values,
    handleChange,
    handleBlur,
    handleSubmit,
    errors,
    touched,
    resetForm,
    dirty,
    isValid,
    setFieldValue,
  } = useFormik({
    initialValues: {
      category: 2, // Default to general cargo
      date_of_arrival: '',
      no_of_containers: '',
      single_item_title: '',
      type: null,
      clearing_discharge_port: '',
      email: '',
      phone: '',
      destination: '',
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      if (values.category === 1) {
        values.tonnage = undefined;
        values.no_of_containers = undefined;
      }

      if (values.category === 2) {
        values.vehicle = undefined;
        values.tonnage = undefined;
      }

      if (values.category === 3) {
        values.no_of_containers = undefined;
        values.vehicle = undefined;
      }
      
      // Set job_type to 'both' for both service
      values.job_type = 'both';
      
      // Also set port_of_discharge to the same value as clearing_discharge_port
      values.port_of_discharge = values.clearing_discharge_port;
      
      onSubmit(values, resetForm);
    },
  });

  // Handle change for clearing_discharge_port to also update port_of_discharge
  const handleClearingPortChange = (e) => {
    handleChange(e);
    // Also update port_of_discharge with the same value
    setFieldValue('port_of_discharge', e.target.value);
  };

  return (
    <form onSubmit={handleSubmit} id="service-form">
      <Row>
        <Col md={6} lg={3} className="mt">
          <label style={{ marginBottom: '0px' }}>Select Category</label>
          <select
            name="category"
            className={`form-control ${touched.category && errors.category ? 'is-invalid' : ''}`}
            onChange={(e) => {
              handleChange(e);
            }}
            value={values.category}
          >
            {Object.keys(serviceData.job.category)
              .filter((key) => key != 1)
              .map((key) => (
                <option value={key} key={key}>
                  {capitalize(serviceData.job.category[key])}
                </option>
              ))}
          </select>
          <span className="text-danger">{touched.category && errors.category}</span>
        </Col>

        <Col md={6} lg={3} className="mt">
          <label style={{ marginBottom: '0px' }}>Items Name {'(Separated by commas) '}</label>
          <input
            name="single_item_title"
            type="text"
            className={`form-control ${
              touched.single_item_title && errors.single_item_title ? 'is-invalid' : ''
            }`}
            placeholder="item 1, item 2"
            onChange={handleChange}
            value={values.single_item_title}
          />
          <span className="text-danger">{touched.single_item_title && errors.single_item_title}</span>
        </Col>

        <Col md={6} lg={3} className="mt">
          <label style={{ marginBottom: '0px' }}>Port of Discharge</label>
          <select
            name="clearing_discharge_port"
            type="text"
            className={`form-control ${
              touched.clearing_discharge_port && errors.clearing_discharge_port ? 'is-invalid' : ''
            }`}
            onChange={handleClearingPortChange}
            value={values.clearing_discharge_port}
          >
            <option value="">Select Port</option>
            {serviceData?.ports?.length > 0 &&
              serviceData?.ports.map((obj) => (
                <option value={obj.uuid} key={obj.uuid}>
                  {startCase(camelCase(obj.port_of_operation))}
                </option>
              ))}
          </select>
          <span className="text-danger">
            {touched.clearing_discharge_port && errors.clearing_discharge_port}
          </span>
        </Col>

        <Col md={6} lg={3} className="mt">
          <label style={{ marginBottom: '0px' }}>Expected Date of Arrival</label>
          <input
            name="date_of_arrival"
            type="date"
            className={`form-control ${
              touched.date_of_arrival && errors.date_of_arrival ? 'is-invalid' : ''
            }`}
            onChange={handleChange}
            value={values.date_of_arrival}
            min={new Date().toISOString().split('T')[0]}
          />
          <span className="text-danger">{touched.date_of_arrival && errors.date_of_arrival}</span>
        </Col>

        {values.category !== 1 && (
          <Col md={6} lg={3} className="mt">
            <label style={{ marginBottom: '0px' }} htmlFor="destination">
              Destination
            </label>
            <Autocomplete
              name="destination"
              id="destination"
              placeholder="Full Address"
              value={values.destination}
              options={{
                types: [],
                componentRestrictions: {
                  country: 'ng',
                },
              }}
              onChange={handleChange}
              onBlur={handleBlur}
              apiKey={configObject.GOOGLE_API_KEY}
              onPlaceSelected={(place) => {
                setFieldValue('destination', place.formatted_address);
              }}
              className={`form-control ${
                touched.destination && errors.destination ? 'is-invalid' : ''
              }`}
              style={{
                height: '50px',
              }}
            />
            <span className="text-danger">{touched.destination && errors.destination}</span>
          </Col>
        )}
        <Col md={6} lg={values.category !== 1 ? 3 : 4} className="mt">
          <label style={{ marginBottom: '0px' }}>Email</label>
          <input
            name="email"
            type="email"
            placeholder="Enter your Email"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.email}
            className={`form-control ${touched.email && errors.email ? 'is-invalid' : ''}`}
          />
          <span className="text-danger">{touched.email && errors.email}</span>
        </Col>

        <Col md={6} lg={values.category !== 1 ? 3 : 4} className="mt position-relative">
          <label style={{ marginBottom: '0px' }}>Phone</label>
          <PhoneInput
            country={'ng'}
            value={values.phone}
            onChange={(x) => setFieldValue('phone', '+' + x)}
            placeholder="Enter your Phone"
            inputStyle={{
              height: '50px',
              width: '100%',
            }}
          />
        </Col>
        <Col md={6} lg={values.category !== 1 ? 3 : 4} className="mt">
          <div className="text-right">
            <button className="post-button w-100" type="submit" disabled={!(isValid && dirty)}>
              Submit
            </button>
          </div>
        </Col>
      </Row>
    </form>
  );
};

export default BothServiceTab;